import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ModeSelectionModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const modes = [
    {
      name: 'PARTY',
      path: '/party',
      color: 'from-cyan-500 via-blue-500 to-purple-500',
      borderColor: 'border-cyan-500/50',
      textColor: 'text-cyan-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(180,70%,60%/0.6)]',
      description: '3D Spline Orb',
    },
    {
      name: 'TRANCE',
      path: '/trance',
      color: 'from-purple-500 via-pink-500 to-purple-600',
      borderColor: 'border-purple-500/50',
      textColor: 'text-purple-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(270,70%,60%/0.6)]',
      description: 'Rainbow Grid',
    },
    {
      name: 'COSMIC',
      path: '/cosmic',
      color: 'from-pink-500 via-red-500 to-orange-500',
      borderColor: 'border-pink-500/50',
      textColor: 'text-pink-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(320,70%,60%/0.6)]',
      description: 'Particle System',
    },
    {
      name: 'SHROOMS',
      path: '/shrooms',
      color: 'from-orange-500 via-red-500 to-pink-500',
      borderColor: 'border-orange-500/50',
      textColor: 'text-orange-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(30,70%,60%/0.6)]',
      description: 'Fire Spiral',
    },
    {
      name: 'LSD',
      path: '/lsd',
      color: 'from-cyan-400 via-blue-500 to-green-400',
      borderColor: 'border-cyan-500/50',
      textColor: 'text-cyan-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(180,70%,60%/0.6)]',
      description: 'Singularity',
    },
    {
      name: 'CRUSADER',
      path: '/crusader',
      color: 'from-amber-600 via-orange-500 to-yellow-600',
      borderColor: 'border-amber-500/50',
      textColor: 'text-amber-400',
      hoverGlow: 'hover:shadow-[0_0_30px_hsl(45,70%,60%/0.6)]',
      description: 'Parallax Journey',
    },
  ];

  const handleModeClick = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto backdrop-blur-xl border border-primary/20 rounded-2xl p-8 md:p-8 p-5 shadow-2xl" style={{ background: 'linear-gradient(135deg, rgba(0, 255, 65, 0.05), rgba(255, 136, 0, 0.05), rgba(139, 69, 19, 0.05))' }}>
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-foreground/60 hover:text-foreground transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Title */}
              <div className="text-center mb-8">
                <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-wider mb-2">
                  <span className="korov-gradient-text">
                    CHOOSE YOUR PATH
                  </span>
                </h2>
                <p className="text-muted-foreground text-sm tracking-widest">
                  SELECT YOUR VISUAL JOURNEY
                </p>
              </div>

              {/* Mode Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {modes.map((mode, index) => (
                  <motion.button
                    key={mode.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleModeClick(mode.path)}
                    className={`group relative overflow-hidden rounded-xl border ${mode.borderColor} bg-black/40 p-6 transition-all duration-500 ${mode.hoverGlow} hover:scale-105 hover:border-opacity-100`}
                  >
                    {/* Background Gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${mode.color} opacity-0 group-hover:opacity-20 transition-opacity duration-500`} />

                    {/* Content */}
                    <div className="relative z-10">
                      <div className="flex items-center justify-center mb-4">
                        <Zap className={`w-8 h-8 ${mode.textColor} group-hover:animate-pulse`} />
                      </div>
                      <h3 className={`text-2xl font-heading font-bold tracking-wider mb-2 ${mode.textColor} group-hover:text-glow transition-all duration-300`}>
                        {mode.name}
                      </h3>
                      <p className="text-muted-foreground text-sm group-hover:text-foreground transition-colors">
                        {mode.description}
                      </p>
                    </div>

                    {/* Animated Border */}
                    <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${mode.color} blur-md`} />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ModeSelectionModal;
